import {
  DEFAULT_TIMEZONE,
  type ParticipationValue,
  type PickLanguageValue,
} from '@yoyoclub/shared';
import { Calendar, ChevronDown, Heart, MapPin, Share2, ThumbsDown, ThumbsUp } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useParams } from 'react-router-dom';

import PickReviewSheet from '../components/picks/PickReviewSheet';
import PicksMark from '../components/picks/PicksMark';
import { useSiteActions } from '../components/site/siteActionsContext';
import {
  btnGhostSm,
  btnPrimary,
  btnPrimarySm,
  focusRing,
  textLink,
} from '../components/ui/redesign';
import { useToast } from '../components/ui/toastContext';
import { useAuth } from '../hooks/useAuth';
import { listSeparator, localize } from '../i18n/script';
import { reviewApi, type MyPickReviewDraft, type ReviewStatus } from '../services/reviewApi';
import { downloadIcs, googleCalendarUrl, icsContent, type CalendarEvent } from '../utils/calendar';
import { takePendingAction } from '../utils/pendingAction';
import { logPickClick, withPicksUtm } from '../utils/pickLinks';

interface Occurrence {
  id: string;
  startTime: string;
  endTime: string;
  noteCN: string | null;
  noteEN: string | null;
  ended: boolean;
}

interface PickDetailData {
  id: string;
  slug: string;
  type: 'PROVIDER' | 'EVENT';
  titleCN: string | null;
  titleEN: string | null;
  providerName: string;
  category: string;
  cityCN: string | null;
  cityEN: string | null;
  /** Legacy single city, read only while a row has no pair yet (deploy window). */
  city?: string | null;
  region: string[];
  addressLine: string | null;
  ageMin: number;
  ageMax: number;
  participation: ParticipationValue;
  language: PickLanguageValue;
  priceNoteCN: string | null;
  priceNoteEN: string | null;
  scheduleNoteCN: string | null;
  scheduleNoteEN: string | null;
  whyPickedCN: string | null;
  whyPickedEN: string | null;
  descriptionCN: string | null;
  descriptionEN: string | null;
  externalBookingNoteCN: string | null;
  externalBookingNoteEN: string | null;
  externalUrl: string;
  recommendCount: number;
  notRecommendCount: number;
  occurrences: Occurrence[];
  ended: boolean;
  recommendedBy: { name: string | null } | null;
}

interface PickReviewItem {
  id: string;
  verdict: 'RECOMMEND' | 'NOT_RECOMMEND';
  content: string;
  visitedAs: 'PROVIDER' | 'EVENT' | null;
  displayName: string | null;
  anonymous: boolean;
  publishedAt: string;
  adminReply: string | null;
}

const api = () => import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';

const sectionTitle = 'mb-2.5 font-ui text-[12.5px] font-semibold tracking-[0.08em] text-mute';

/**
 * /picks/:slug (PRD §5.3, §5.7, §5.9, prototype `renderPick`). Text only, no photos;
 * the main button opens the provider's page in a new window.
 */
export default function PickDetail() {
  const { slug = '' } = useParams();
  const { t, i18n } = useTranslation(['picks', 'common', 'review']);
  const location = useLocation();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { isFavorite, toggleFavorite, share, requireLogin } = useSiteActions();
  const [pick, setPick] = useState<PickDetailData | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'missing' | 'failed'>('loading');
  const [reviews, setReviews] = useState<PickReviewItem[]>([]);
  const [reviewPage, setReviewPage] = useState({ page: 1, hasMore: false, loading: false });
  const [calOpen, setCalOpen] = useState<string | null>(null);
  const toast = useToast();
  const [myReview, setMyReview] = useState<
    | (MyPickReviewDraft & { id: string; status: ReviewStatus; rejectionReason?: string | null })
    | null
  >(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [showEnded, setShowEnded] = useState(false);
  const locale = i18n.language;

  useEffect(() => {
    const controller = new AbortController();
    setState('loading');
    fetch(`${api()}/picks/${encodeURIComponent(slug)}`, { signal: controller.signal })
      .then(async (res) => {
        if (res.status === 404) return setState('missing');
        const body = await res.json();
        if (!body?.success) throw new Error('load failed');
        setPick(body.data);
        setState('ready');
      })
      .catch((err: unknown) => {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setState('failed');
      });
    return () => controller.abort();
  }, [slug]);

  useEffect(() => {
    if (!pick) return;
    const controller = new AbortController();
    fetch(`${api()}/picks/${encodeURIComponent(pick.id)}/reviews`, { signal: controller.signal })
      .then((res) => res.json())
      .then((body) => {
        if (!body?.success) return;
        setReviews(body.data.items);
        setReviewPage({
          page: 1,
          hasMore: body.data.page * body.data.pageSize < body.data.total,
          loading: false,
        });
      })
      .catch(() => {
        // Reviews are secondary; the page works without them.
      });
    return () => controller.abort();
  }, [pick]);

  // The signed-in parent's own review of this Pick, to edit it or see its status.
  const loadMyReview = useCallback(() => {
    if (!pick || !isAuthenticated) {
      setMyReview(null);
      return;
    }
    reviewApi
      .pickMineForPick(pick.id)
      .then(setMyReview)
      .catch(() => setMyReview(null));
  }, [pick, isAuthenticated]);
  useEffect(loadMyReview, [loadMyReview]);

  // After sign-in, open the review sheet the visitor asked for.
  useEffect(() => {
    if (!pick || authLoading || !isAuthenticated) return;
    const action = takePendingAction(
      `${location.pathname}${location.search}${location.hash}`,
      'review'
    );
    if (action && action.pickId === pick.id) setReviewOpen(true);
  }, [pick, authLoading, isAuthenticated, location]);

  // After sign-in, reopen the calendar choice the visitor asked for (PRD F1).
  useEffect(() => {
    if (!pick || authLoading || !isAuthenticated) return;
    const action = takePendingAction(
      `${location.pathname}${location.search}${location.hash}`,
      'calendar'
    );
    if (action && action.pickId === pick.id) setCalOpen(action.occurrenceId);
  }, [pick, authLoading, isAuthenticated, location]);

  const loadMoreReviews = () => {
    if (!pick || reviewPage.loading) return;
    const next = reviewPage.page + 1;
    setReviewPage((s) => ({ ...s, loading: true }));
    fetch(`${api()}/picks/${encodeURIComponent(pick.id)}/reviews?page=${next}`)
      .then((res) => res.json())
      .then((body) => {
        if (!body?.success) throw new Error('load failed');
        setReviews((prev) => [
          ...prev,
          ...body.data.items.filter((r: PickReviewItem) => !prev.some((p) => p.id === r.id)),
        ]);
        setReviewPage({
          page: next,
          hasMore: body.data.page * body.data.pageSize < body.data.total,
          loading: false,
        });
      })
      .catch(() => setReviewPage((s) => ({ ...s, loading: false })));
  };

  const openReview = () => {
    if (!pick) return;
    if (!requireLogin({ type: 'review', pickId: pick.id }, 'review')) return;
    setReviewOpen(true);
  };

  const openCalendar = (occurrenceId: string) => {
    if (!pick) return;
    if (!requireLogin({ type: 'calendar', pickId: pick.id, occurrenceId }, 'calendar')) return;
    setCalOpen((cur) => (cur === occurrenceId ? null : occurrenceId));
  };

  if (state === 'loading') return <div className="min-h-[60vh] bg-warm-white" aria-busy="true" />;
  if (state !== 'ready' || !pick) {
    return (
      <div className="bg-warm-white">
        <div className="mx-auto max-w-[760px] px-4 py-16 text-center text-ink-2">
          <p>{state === 'missing' ? t('picks:detail.notFound') : t('picks:list.loadFailed')}</p>
          <Link to="/picks" className={`${textLink} mt-4`}>
            {t('picks:detail.back')}
          </Link>
        </div>
      </div>
    );
  }

  const title = localize(pick.titleCN, pick.titleEN, locale);
  const why = localize(pick.whyPickedCN, pick.whyPickedEN, locale);
  const description = localize(pick.descriptionCN, pick.descriptionEN, locale);
  const bookingNote = localize(pick.externalBookingNoteCN, pick.externalBookingNoteEN, locale);
  const price = localize(pick.priceNoteCN, pick.priceNoteEN, locale);
  const regionText = pick.region.map((r) => t(`common:region.${r}`)).join(listSeparator(locale));
  const city = localize(pick.cityCN, pick.cityEN, locale) || pick.city;
  // §5.3: only the parts that exist, no empty separators.
  const placeLine = [pick.addressLine || city, regionText].filter(Boolean).join(' · ');
  const calendarPlace = [pick.addressLine, city, regionText].filter(Boolean).join(', ');
  const href = `/picks/${pick.slug}`;
  const saved = isFavorite('pick', pick.id);
  const ageText =
    pick.ageMin >= 18
      ? t('common:card.adults')
      : pick.ageMax >= 99
        ? t('common:card.agePlus', { min: pick.ageMin })
        : t('common:card.ageRange', { min: pick.ageMin, max: pick.ageMax });
  const fmtDate = (iso: string) =>
    new Date(iso).toLocaleDateString(locale, {
      month: 'long',
      day: 'numeric',
      weekday: 'short',
      timeZone: DEFAULT_TIMEZONE,
    });
  const fmtTime = (iso: string) =>
    new Date(iso).toLocaleTimeString(locale, {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: DEFAULT_TIMEZONE,
    });
  const upcoming = pick.occurrences.filter((o) => !o.ended);
  const past = pick.occurrences.filter((o) => o.ended);
  const total = pick.recommendCount + pick.notRecommendCount;

  const calendarEvent = (o: Occurrence): CalendarEvent => {
    const url = `${window.location.origin}${href}`;
    return {
      uid: o.id,
      title,
      start: new Date(o.startTime),
      end: new Date(o.endTime),
      location: calendarPlace,
      description: [why, url, t('picks:detail.calendarNote', { provider: pick.providerName })]
        .filter(Boolean)
        .join('\n\n'),
      url,
    };
  };

  return (
    <div className="bg-warm-white font-text text-[15px] leading-[1.6] text-ink">
      <Helmet>
        <title>{t('picks:meta.detailTitle', { title })}</title>
        {why && <meta name="description" content={why.slice(0, 150)} />}
        {pick.ended && <meta name="robots" content="noindex" />}
      </Helmet>

      <section className="mx-auto max-w-[1160px] px-4 pt-[22px] desk:px-7">
        <Link to="/picks" className={textLink}>
          {t('picks:detail.back')}
        </Link>
      </section>

      <section className="mx-auto grid max-w-[1160px] items-start gap-2.5 px-4 pb-[50px] pt-[18px] desk:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)] desk:gap-[34px] desk:px-7">
        <div>
          <div>
            <PicksMark />
            <span className="ml-2 text-[12.5px] text-mute">
              {pick.type === 'EVENT' ? t('picks:card.event') : t('picks:card.provider')} ·{' '}
              {t(`common:category.${pick.category}`)}
            </span>
            <h1 className="mb-1.5 mt-3 font-ui text-[clamp(26px,3vw,36px)] font-bold leading-[1.25] text-ink [text-wrap:balance]">
              {title}
              {pick.ended && (
                <span className="ml-2.5 inline-block rounded-full bg-foot px-2.5 py-0.5 align-middle font-ui text-[13px] font-semibold text-mute">
                  {t('picks:detail.ended')}
                </span>
              )}
            </h1>
            <div className="text-[15px] text-ink-2">
              {t('picks:card.from', { provider: pick.providerName })}
            </div>
            {pick.recommendedBy && (
              <div className="mt-1 text-[13px] text-mute">
                {pick.recommendedBy.name
                  ? t('picks:detail.recommendedBy', { name: pick.recommendedBy.name })
                  : t('picks:detail.recommendedByParent')}
              </div>
            )}
          </div>

          {why && (
            <div className="mb-1.5 mt-[18px] rounded-2xl bg-primary-pale px-4 py-3.5 text-[15px] leading-[1.65] text-[#33463A]">
              <b className="mb-0.5 block font-ui text-[13px] text-primary-dark">
                {t('picks:detail.why')}
              </b>
              {why}
            </div>
          )}

          <div className="mt-6">
            <h3 className={sectionTitle}>{t('picks:detail.time')}</h3>
            {pick.type === 'PROVIDER' ? (
              <OccurrenceRow
                title={localize(pick.scheduleNoteCN, pick.scheduleNoteEN, locale)}
                sub={t('picks:detail.ongoing')}
              />
            ) : (
              <>
                {upcoming.map((o) => (
                  <OccurrenceRow
                    key={o.id}
                    title={fmtDate(o.startTime)}
                    sub={[
                      `${fmtTime(o.startTime)} ${t('picks:detail.to')} ${fmtTime(o.endTime)}`,
                      localize(o.noteCN, o.noteEN, locale),
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  >
                    <div className="relative">
                      <button
                        type="button"
                        className={btnGhostSm}
                        aria-expanded={calOpen === o.id}
                        onClick={() => openCalendar(o.id)}
                      >
                        <Calendar className="h-4 w-4" aria-hidden="true" />
                        {t('picks:detail.addToCalendar')}
                      </button>
                      {calOpen === o.id && (
                        <CalendarChoices
                          event={calendarEvent(o)}
                          onClose={() => setCalOpen(null)}
                          onChosen={() => void logPickClick(pick.id, 'CALENDAR')}
                          filename={`${pick.slug}.ics`}
                        />
                      )}
                    </div>
                  </OccurrenceRow>
                ))}
                {past.length > 0 && (
                  <button
                    type="button"
                    className={`${textLink} mt-1`}
                    aria-expanded={showEnded}
                    onClick={() => setShowEnded((v) => !v)}
                  >
                    {t('picks:detail.pastDates', { count: past.length })}
                    <ChevronDown
                      className={`h-4 w-4 transition-transform ${showEnded ? 'rotate-180' : ''}`}
                      aria-hidden="true"
                    />
                  </button>
                )}
                {showEnded &&
                  past.map((o) => (
                    <OccurrenceRow
                      key={o.id}
                      title={fmtDate(o.startTime)}
                      sub={`${fmtTime(o.startTime)} ${t('picks:detail.to')} ${fmtTime(o.endTime)}`}
                      muted
                    />
                  ))}
              </>
            )}
          </div>

          <div className="mt-6 grid grid-cols-2 gap-2.5">
            {[
              [t('picks:detail.price'), price || '-'],
              [t('picks:detail.age'), ageText],
              [
                t('picks:detail.language'),
                pick.language === 'NA' ? '-' : t(`common:teachingLanguage.${pick.language}`),
              ],
              [t('picks:detail.participation'), t(`common:participation.${pick.participation}`)],
            ].map(([k, v]) => (
              <div key={k} className="rounded-[14px] border border-line bg-white px-3.5 py-[11px]">
                <div className="font-ui text-[12px] text-mute">{k}</div>
                <div className="mt-px text-[15px] font-bold">{v}</div>
              </div>
            ))}
          </div>

          {placeLine && (
            <div className="mt-6">
              <h3 className={sectionTitle}>{t('picks:detail.place')}</h3>
              {pick.addressLine ? (
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(pick.addressLine)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`flex items-start gap-3 rounded-[14px] border border-line bg-white px-3.5 py-[13px] hover:border-primary ${focusRing}`}
                >
                  <MapPin
                    className="mt-0.5 h-[18px] w-[18px] flex-none text-primary"
                    aria-hidden="true"
                  />
                  <div>
                    <div className="font-bold">{pick.providerName}</div>
                    <div className="text-[12.5px] text-mute">{placeLine}</div>
                    <span className="mt-1 inline-flex font-ui text-[13.5px] font-semibold text-primary">
                      {t('picks:detail.openMap')}
                    </span>
                  </div>
                </a>
              ) : (
                <div className="flex items-start gap-3 rounded-[14px] border border-line bg-white px-3.5 py-[13px]">
                  <MapPin
                    className="mt-0.5 h-[18px] w-[18px] flex-none text-primary"
                    aria-hidden="true"
                  />
                  <div>
                    <div className="font-bold">{pick.providerName}</div>
                    <div className="text-[12.5px] text-mute">{placeLine}</div>
                  </div>
                </div>
              )}
            </div>
          )}

          {(description || bookingNote) && (
            <div className="mt-6">
              <h3 className={sectionTitle}>{t('picks:detail.about')}</h3>
              {description && <p className="whitespace-pre-line text-ink-2">{description}</p>}
              {bookingNote && (
                <p className="mt-2 whitespace-pre-line text-[12.5px] text-mute">{bookingNote}</p>
              )}
            </div>
          )}

          <div className="mt-6">
            <h3 className={sectionTitle}>{t('picks:detail.reviews')}</h3>
            <div className="flex flex-wrap items-center gap-4 rounded-2xl border border-line bg-white px-4 py-3.5">
              <div>
                <div className="font-ui text-[22px] font-bold text-primary-dark">
                  {pick.recommendCount}
                </div>
                <div className="text-[13px] text-mute">{t('picks:detail.recommendLabel')}</div>
              </div>
              <div className="h-2 min-w-[120px] flex-1 overflow-hidden rounded-[8px] bg-[#EADFD2]">
                <i
                  className="block h-full bg-primary"
                  style={{
                    width: `${total ? Math.round((pick.recommendCount / total) * 100) : 0}%`,
                  }}
                />
              </div>
              <div>
                <div className="font-ui text-[22px] font-bold text-brown">
                  {pick.notRecommendCount}
                </div>
                <div className="text-[13px] text-mute">{t('picks:detail.notRecommendLabel')}</div>
              </div>
              <button type="button" className={btnPrimarySm} onClick={openReview}>
                {myReview ? t('picks:review.editMine') : t('picks:review.write')}
              </button>
            </div>
            {myReview?.status === 'pending' && (
              <p className="mt-2 text-[12.5px] text-mute">{t('picks:review.pendingNote')}</p>
            )}
            {myReview?.status === 'rejected' && (
              <p className="mt-2 text-[12.5px] text-red-700">
                {t('picks:review.rejectedNote', { reason: myReview.rejectionReason ?? '' })}
              </p>
            )}
            {reviews.length === 0 ? (
              <p className="mt-3 text-[12.5px] text-mute">{t('picks:detail.noReviews')}</p>
            ) : (
              reviews.map((r) => (
                <div key={r.id} className="border-b border-line py-4">
                  <div className="flex flex-wrap items-center gap-x-2.5 gap-y-2 text-[13px] text-mute">
                    <span
                      className={`inline-flex items-center gap-[5px] rounded-full px-2.5 py-[3px] font-ui text-[12.5px] font-semibold ${
                        r.verdict === 'RECOMMEND'
                          ? 'bg-primary-pale text-primary-dark'
                          : 'bg-[#F1E7DF] text-brown'
                      }`}
                    >
                      {r.verdict === 'RECOMMEND' ? (
                        <ThumbsUp className="h-3.5 w-3.5" aria-hidden="true" />
                      ) : (
                        <ThumbsDown className="h-3.5 w-3.5" aria-hidden="true" />
                      )}
                      {r.verdict === 'RECOMMEND'
                        ? t('picks:detail.recommend')
                        : t('picks:detail.notRecommend')}
                    </span>
                    <span className="text-sm font-semibold text-ink">
                      {r.anonymous || !r.displayName ? t('picks:detail.anonymous') : r.displayName}
                    </span>
                    {r.visitedAs && (
                      <span className="rounded-full bg-warm-white px-2 py-0.5 text-[12px]">
                        {r.visitedAs === 'EVENT'
                          ? t('picks:visited.EVENT')
                          : t('picks:visited.PROVIDER')}
                      </span>
                    )}
                    <span>
                      {new Date(r.publishedAt).toLocaleDateString(locale, {
                        month: 'long',
                        day: 'numeric',
                        timeZone: DEFAULT_TIMEZONE,
                      })}
                    </span>
                  </div>
                  <p className="mt-2 whitespace-pre-line text-[14.5px] leading-[1.7] text-ink-2">
                    {r.content}
                  </p>
                  {r.adminReply && (
                    <div className="mt-2.5 rounded-[12px] bg-warm-white px-3 py-2.5 text-[13.5px] text-ink-2">
                      <b className="mr-1.5 font-ui text-[12.5px] text-primary-dark">
                        {t('picks:detail.official')}
                      </b>
                      {r.adminReply}
                    </div>
                  )}
                </div>
              ))
            )}
            {reviewPage.hasMore && (
              <button
                type="button"
                className={`${btnGhostSm} mt-4`}
                disabled={reviewPage.loading}
                onClick={loadMoreReviews}
              >
                {t('review:loadMore')}
              </button>
            )}
          </div>
        </div>

        <aside className="rounded-[22px] border border-line bg-white p-5 desk:sticky desk:top-[calc(env(safe-area-inset-top,0px)+86px)]">
          <a
            href={withPicksUtm(pick.externalUrl)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => void logPickClick(pick.id, 'LINK')}
            className={`${btnPrimary} w-full`}
          >
            {t('picks:detail.goTo', { provider: pick.providerName })}
          </a>
          <div className="mt-2.5 flex gap-2">
            <button
              type="button"
              className={`${btnGhostSm} flex-1 ${saved ? '!text-heart' : ''}`}
              aria-pressed={saved}
              onClick={() => toggleFavorite('pick', pick.id)}
            >
              <Heart className={`h-4 w-4 ${saved ? 'fill-current' : ''}`} aria-hidden="true" />
              {saved ? t('common:favorite.saved') : t('common:favorite.add')}
            </button>
            <button
              type="button"
              className={`${btnGhostSm} flex-1`}
              onClick={() => share(href, title)}
            >
              <Share2 className="h-4 w-4" aria-hidden="true" />
              {t('common:share.button')}
            </button>
          </div>
          <p className="mt-3.5 text-[12.5px] leading-[1.55] text-mute">
            {t('picks:detail.disclaimer')}
          </p>
        </aside>
      </section>

      <PickReviewSheet
        open={reviewOpen}
        pickId={pick.id}
        pickType={pick.type}
        existing={myReview}
        onClose={() => setReviewOpen(false)}
        onSaved={(kind) => {
          setReviewOpen(false);
          toast(kind === 'created' ? t('review:notice.created') : t('review:notice.updated'));
          loadMyReview();
        }}
      />
    </div>
  );
}

function OccurrenceRow({
  title,
  sub,
  muted = false,
  children,
}: {
  title: string;
  sub: string;
  muted?: boolean;
  children?: React.ReactNode;
}) {
  return (
    <div
      className={`mb-2 flex flex-wrap items-center justify-between gap-3 rounded-[14px] border border-line bg-white px-3.5 py-[11px] ${
        muted ? 'opacity-60' : ''
      }`}
    >
      <div className="font-ui text-[15px] font-semibold">
        {title}
        {sub && (
          <small className="block font-text text-[12.5px] font-normal text-mute">{sub}</small>
        )}
      </div>
      {children}
    </div>
  );
}

/** Google Calendar or .ics (F7); a choice counts as one CALENDAR click, opening does not. */
function CalendarChoices({
  event,
  filename,
  onChosen,
  onClose,
}: {
  event: CalendarEvent;
  filename: string;
  onChosen: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation('picks');
  const ref = useRef<HTMLDivElement>(null);
  const close = useCallback(onClose, [onClose]);

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.parentElement?.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close();
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [close]);

  const item = `flex min-h-[44px] w-full items-center gap-[9px] rounded-[10px] px-3 py-2.5 text-left text-sm hover:bg-warm-white ${focusRing}`;
  return (
    <div
      ref={ref}
      role="menu"
      className="absolute right-0 top-[calc(100%+6px)] z-30 min-w-[220px] rounded-[14px] border border-line bg-white p-1.5 shadow-[0_18px_40px_-20px_rgba(0,0,0,0.45)]"
    >
      <a
        role="menuitem"
        href={googleCalendarUrl(event)}
        target="_blank"
        rel="noopener noreferrer"
        className={item}
        onClick={() => {
          onChosen();
          onClose();
        }}
      >
        <Calendar className="h-[18px] w-[18px]" aria-hidden="true" />
        {t('detail.googleCalendar')}
      </a>
      <button
        type="button"
        role="menuitem"
        className={item}
        onClick={() => {
          downloadIcs(filename, icsContent(event));
          onChosen();
          onClose();
        }}
      >
        <Calendar className="h-[18px] w-[18px]" aria-hidden="true" />
        {t('detail.icsCalendar')}
      </button>
    </div>
  );
}
